// ─────────────────────────────────────────────────────────────────────────────
// Process Manufacturing — Outputs & Costing Page
// Output receipt, NRV cost rollup execution, variance analysis
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { Card } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/Select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/shared/components/ui/Dialog';
import { Badge } from '@/shared/components/ui/Badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/shared/components/ui/Table';
import { Search, Calculator, CheckCircle, FileText } from 'lucide-react';
import { useApi } from '@/shared/hooks/useApi';

interface CostAllocation {
  _id: string;
  allocationNumber: string;
  batchRecordId: string;
  orderId: string;
  method: string;
  totalJointCost: number;
  byProductCredits: any[];
  totalByProductCredits: number;
  netJointCost: number;
  allocations: Array<{
    lineNumber: number;
    productId: string;
    productName: string;
    type: string;
    qty: number;
    allocationBasis: number;
    allocationPct: number;
    allocatedCost: number;
    unitCost: number;
    standardUnitCost: number;
    costVariance: number;
    yieldVariance: number;
  }>;
  status: string;
  createdAt: string;
}

export function OutputsCostingPage() {
  const [allocations, setAllocations] = useState<CostAllocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [viewAllocation, setViewAllocation] = useState<CostAllocation | null>(null);
  const { get } = useApi();
  
  useEffect(() => {
    fetchAllocations();
  }, [get]);
  
  async function fetchAllocations() {
    setLoading(true);
    try {
      const res = await get('/api/manufacturing/costing/allocations');
      setAllocations(res.allocations || []);
    } catch (error) {
      console.error('Failed to fetch allocations:', error);
    } finally {
      setLoading(false);
    }
  }
  
  function handleView(allocation: CostAllocation) {
    setViewAllocation(allocation);
  }
  
  const filteredAllocations = allocations.filter(a => 
    a.allocationNumber.toLowerCase().includes(search.toLowerCase())
  ).filter(a => !filterStatus || a.status === filterStatus);
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Joint Costing & Rollup</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Output receipt, NRV cost rollup execution, variance analysis</p>
        </div>
        <Button variant="outline" onClick={() => window.location.href = '/manufacturing/costing/compare'}>
          <Calculator className="w-4 h-4 mr-2" />
          Compare Methods
        </Button>
      </div>
      
      <div className="flex gap-4 mb-4">
        <Input placeholder="Search allocations..." value={search} onChange={e => setSearch(e.target.value)} className="w-64" leftIcon={<Search className="w-4 h-4" />} />
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="calculated">Calculated</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="posted">Posted</SelectItem>
            <SelectItem value="reversed">Reversed</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {loading ? (
        <Card className="p-8 text-center">Loading...</Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Allocation #</TableHead>
                <TableHead>Batch #</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Total Joint Cost</TableHead>
                <TableHead>By-Product Credits</TableHead>
                <TableHead>Net Joint Cost</TableHead>
                <TableHead>Products</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="w-40">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAllocations.map(allocation => (
                <TableRow key={allocation._id}>
                  <TableCell className="font-mono font-medium">{allocation.allocationNumber}</TableCell>
                  <TableCell className="font-mono text-sm">{allocation.batchRecordId}</TableCell>
                  <TableCell><Badge variant="secondary">{allocation.method.toUpperCase()}</Badge></TableCell>
                  <TableCell className="font-mono">{(allocation.totalJointCost / 100).toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                  <TableCell className="font-mono text-green-600">-{(allocation.totalByProductCredits / 100).toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                  <TableCell className="font-mono font-medium">{(allocation.netJointCost / 100).toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                  <TableCell>{allocation.allocations.length} outputs</TableCell>
                  <TableCell>
                    <Badge variant={allocation.status === 'approved' || allocation.status === 'posted' ? 'success' : allocation.status === 'draft' ? 'secondary' : 'outline'}>
                      {allocation.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">{allocation.createdAt ? new Date(allocation.createdAt).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" onClick={() => handleView(allocation)} title="View Details">
                        <FileText className="w-4 h-4" />
                      </Button>
                      {allocation.status === 'calculated' && (
                        <Button variant="ghost" size="icon" title="Approve" className="text-green-500">
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                      )}
                      {allocation.status === 'approved' && (
                        <Button variant="ghost" size="icon" title="Post Journals" className="text-blue-500">
                          <Calculator className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filteredAllocations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8 text-gray-500">No cost allocations found. Run a cost rollup from a completed batch.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      )}
      
      {/* Cost Rollup Runner Section */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Calculator className="w-5 h-5" />
                Run Cost Rollup
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mt-1">Execute joint cost allocation for a completed batch. Select allocation method and input cost pool data.</p>
            </div>
            <Button onClick={() => window.location.href = '/manufacturing/costing/run'}>
              <Calculator className="w-4 h-4 mr-2" />
              Run Allocation
            </Button>
          </div>
          
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="p-4 bg-white dark:bg-gray-800">
              <h4 className="font-medium mb-2">Allocation Methods</h4>
              <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                <li>• Net Realizable Value (NRV)</li>
                <li>• Sales Value at Split-Off</li>
                <li>• Physical Quantity (Mass/Volume)</li>
                <li>• Weighted Technical Factor</li>
                <li>• Manual Percentage</li>
              </ul>
            </Card>
            <Card className="p-4 bg-white dark:bg-gray-800">
              <h4 className="font-medium mb-2">By-Product Credit Policies</h4>
              <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                <li>• Deduct NRV (default)</li>
                <li>• Deduct Net Salvage Value</li>
                <li>• No Credit</li>
              </ul>
            </Card>
            <Card className="p-4 bg-white dark:bg-gray-800">
              <h4 className="font-medium mb-2">Variance Analysis</h4>
              <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                <li>• Material Usage & Price</li>
                <li>• Yield Variance</li>
                <li>• Labor Rate & Efficiency</li>
                <li>• Overhead Variance</li>
                <li>• WIP Clearance</li>
              </ul>
            </Card>
          </div>
        </div>
      </Card>
      
      {/* View Allocation Dialog */}
      <Dialog open={!!viewAllocation} onOpenChange={open => !open && setViewAllocation(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Cost Allocation Details: {viewAllocation?.allocationNumber}</DialogTitle>
          </DialogHeader>
          <div className="p-4 space-y-6">
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded">
                <p className="text-gray-500">Total Joint Cost</p>
                <p className="font-bold text-lg">{((viewAllocation?.totalJointCost ?? 0) / 100).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded">
                <p className="text-gray-500">By-Product Credits</p>
                <p className="font-bold text-lg text-green-600">- {((viewAllocation?.totalByProductCredits ?? 0) / 100).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded">
                <p className="text-gray-500">Net Joint Cost</p>
                <p className="font-bold text-lg">{((viewAllocation?.netJointCost ?? 0) / 100).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded">
                <p className="text-gray-500">Method</p>
                <p className="font-bold text-lg capitalize">{viewAllocation?.method}</p>
              </div>
            </div>
            
            {viewAllocation?.byProductCredits.length && (
              <div>
                <h4 className="font-medium mb-2">By-Product Credits</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead>Qty</TableHead>
                      <TableHead>NRV/Unit</TableHead>
                      <TableHead>Total Credit</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {viewAllocation.byProductCredits.map((credit: any, idx: number) => (
                      <TableRow key={idx}>
                        <TableCell>{credit.productName}</TableCell>
                        <TableCell>{credit.qty.toLocaleString()}</TableCell>
                        <TableCell>{(credit.nrvPerUnit / 100).toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                        <TableCell className="text-green-600 font-medium">{(credit.totalCredit / 100).toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            
            <div>
              <h4 className="font-medium mb-2">Co-Product Allocations</h4>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Qty</TableHead>
                    <TableHead>Basis</TableHead>
                    <TableHead>%</TableHead>
                    <TableHead>Allocated Cost</TableHead>
                    <TableHead>Unit Cost</TableHead>
                    <TableHead>Std Unit Cost</TableHead>
                    <TableHead>Cost Var</TableHead>
                    <TableHead>Yield Var</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {viewAllocation?.allocations.map((alloc: any, idx: number) => (
                    <TableRow key={idx}>
                      <TableCell>{alloc.productName}</TableCell>
                      <TableCell><Badge variant={alloc.type === 'by_product' ? 'secondary' : 'outline'}>{alloc.type}</Badge></TableCell>
                      <TableCell>{alloc.qty.toLocaleString()}</TableCell>
                      <TableCell>{alloc.allocationBasis.toLocaleString()}</TableCell>
                      <TableCell>{alloc.allocationPct.toFixed(1)}%</TableCell>
                      <TableCell className="font-medium">{(alloc.allocatedCost / 100).toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                      <TableCell>{(alloc.unitCost / 100).toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                      <TableCell>{(alloc.standardUnitCost / 100).toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                      <TableCell className={alloc.costVariance > 0 ? 'text-red-600' : alloc.costVariance < 0 ? 'text-green-600' : ''}>
                        {(alloc.costVariance / 100).toLocaleString(undefined, {minimumFractionDigits: 2})}
                      </TableCell>
                      <TableCell className={alloc.yieldVariance > 0 ? 'text-red-600' : alloc.yieldVariance < 0 ? 'text-green-600' : ''}>
                        {(alloc.yieldVariance / 100).toLocaleString(undefined, {minimumFractionDigits: 2})}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewAllocation(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}